import { w as bignumber } from './i18n-9ea5877b.js';

bignumber.config({ DECIMAL_PLACES: 24 });
const calculateFeeAndTotal = ({ fees, exchangeRatio, investmentAmount, paymentDecimals }) => {
  let calculatedFee;
  let totalAmount;
  if ((fees === null || fees === void 0 ? void 0 : fees.type) === "FLAT") {
    calculatedFee = new bignumber(fees.value).multipliedBy(exchangeRatio).decimalPlaces(paymentDecimals, bignumber.ROUND_UP);
  }
  else if ((fees === null || fees === void 0 ? void 0 : fees.type) === "PERCENTAGE_AMOUNT_TO_PAY") {
    const feePercent = new bignumber(fees.value).dividedBy(100);
    const feeMultiplier = new bignumber(1).minus(feePercent);
    calculatedFee = investmentAmount.dividedBy(feeMultiplier).minus(investmentAmount).decimalPlaces(paymentDecimals, bignumber.ROUND_UP);
  }
  else if ((fees === null || fees === void 0 ? void 0 : fees.type) === 'PERCENTAGE_INVESTMENT_AMOUNT') {
    const feePercent = new bignumber(fees.value).dividedBy(100);
    calculatedFee = investmentAmount.multipliedBy(feePercent).decimalPlaces(paymentDecimals, bignumber.ROUND_UP);
  }
  else {
    calculatedFee = new bignumber(0);
  }
  totalAmount = calculatedFee.plus(investmentAmount);
  return {
    calculatedFee,
    totalAmount
  };
};
const calculateFromInvestmentAmount = ({ tokenPrice, lastNAV, amount, exchangeRatio, tokenDecimals, tokenMax, tokenMin, investmentMin, investmentMax, fees, paymentDecimals, }) => {
  let tokenAmount;
  let investmentAmount = new bignumber(amount);
  if (!investmentAmount || investmentAmount.isNaN())
    return;
  if (tokenPrice) {
    tokenAmount = investmentAmount.dividedBy(exchangeRatio).dividedBy(tokenPrice).decimalPlaces(tokenDecimals, bignumber.ROUND_DOWN);
    if (tokenAmount.isNaN() || tokenAmount.isGreaterThan(tokenMax) || tokenAmount.isLessThan(tokenMin)) {
      return;
    }
    investmentAmount = tokenAmount.multipliedBy(tokenPrice).multipliedBy(exchangeRatio).decimalPlaces(paymentDecimals, bignumber.ROUND_UP);
  }
  else {
    if (investmentAmount.isGreaterThan(new bignumber(investmentMax).multipliedBy(exchangeRatio)) || investmentAmount.isLessThan(new bignumber(investmentMin).multipliedBy(exchangeRatio))) {
      return;
    }
    if (lastNAV) {
      tokenAmount = investmentAmount.dividedBy(exchangeRatio).dividedBy(lastNAV).decimalPlaces(tokenDecimals, bignumber.ROUND_DOWN);
    }
  }
  return Object.assign({ investmentAmount,
    tokenAmount }, calculateFeeAndTotal({
    fees, exchangeRatio, investmentAmount, paymentDecimals
  }));
};
const calculateFromTokenAmount = ({ amount, tokenMax, tokenMin, exchangeRatio, fees, paymentDecimals, tokenPrice, tokenDecimals, }) => {
  let tokenAmount;
  let investmentAmount;
  const estimatedPrice = tokenPrice;
  tokenAmount = new bignumber(amount).decimalPlaces(tokenDecimals);
  if (tokenAmount.isNaN() || tokenAmount.isGreaterThan(tokenMax) || tokenAmount.isLessThan(tokenMin)) {
    return;
  }
  investmentAmount = tokenAmount.multipliedBy(estimatedPrice).multipliedBy(exchangeRatio).decimalPlaces(paymentDecimals, bignumber.ROUND_UP);
  return Object.assign({ investmentAmount,
    tokenAmount }, calculateFeeAndTotal({
    fees, exchangeRatio, investmentAmount, paymentDecimals
  }));
};
const calculateFromTotalAmount = ({ amount, fees, tokenPrice, lastNAV, exchangeRatio, tokenDecimals, paymentDecimals, tokenMax, tokenMin, investmentMin, investmentMax }) => {
  var _a;
  let totalAmount = new bignumber(amount);
  if (!totalAmount || totalAmount.isNaN()) {
    return;
  }
  if ((fees === null || fees === void 0 ? void 0 : fees.type) === 'FLAT' || !(fees === null || fees === void 0 ? void 0 : fees.type)) {
    const calculatedFee = new bignumber((_a = fees === null || fees === void 0 ? void 0 : fees.value) !== null && _a !== void 0 ? _a : 0).multipliedBy(exchangeRatio).toFixed(paymentDecimals, bignumber.ROUND_UP);
    return calculateFromInvestmentAmount({
      amount: totalAmount.minus(calculatedFee).toString(),
      tokenMax,
      tokenMin,
      investmentMin,
      investmentMax,
      exchangeRatio,
      fees,
      paymentDecimals,
      tokenPrice,
      lastNAV,
      tokenDecimals,
    });
  }
  else if ((fees === null || fees === void 0 ? void 0 : fees.type) === 'PERCENTAGE_AMOUNT_TO_PAY' || (fees === null || fees === void 0 ? void 0 : fees.type) === 'PERCENTAGE_INVESTMENT_AMOUNT') {
    let feeMultiplier;
    if (fees.type === 'PERCENTAGE_AMOUNT_TO_PAY') {
      feeMultiplier = new bignumber(fees.value).dividedBy(100);
    }
    else {
      feeMultiplier = new bignumber(1).minus(new bignumber(1).dividedBy(new bignumber(fees.value).dividedBy(100).plus(1)));
    }
    const calculatedFee = new bignumber(totalAmount).multipliedBy(feeMultiplier);
    const investmentAmount = totalAmount.minus(calculatedFee).decimalPlaces(paymentDecimals, bignumber.ROUND_UP);
    if (tokenPrice || lastNAV) {
      const estimatedPrice = new bignumber(tokenPrice || lastNAV);
      let tokenAmount = investmentAmount.dividedBy(exchangeRatio).dividedBy(estimatedPrice).decimalPlaces(tokenDecimals, bignumber.ROUND_DOWN);
      let numbers = calculateFromTokenAmount({
        amount: tokenAmount.toString(),
        tokenPrice: estimatedPrice.toString(),
        exchangeRatio,
        tokenDecimals,
        paymentDecimals,
        tokenMin,
        tokenMax,
        fees
      });
      if (!numbers)
        return;
      // it's possible that the calculated totalAmount is a tiny amount larger than the entered totalAmount
      // if so, slowly decrement token amount until it isn't
      const oneCent = new bignumber(1).dividedBy(new bignumber(10).pow(paymentDecimals));
      const smallestDecrement = oneCent.dividedBy(exchangeRatio).dividedBy(estimatedPrice);
      while (numbers.totalAmount.isGreaterThan(totalAmount)) {
        tokenAmount = tokenAmount.minus(smallestDecrement);
        numbers = calculateFromTokenAmount({
          amount: tokenAmount.toString(),
          tokenPrice: estimatedPrice.toString(),
          exchangeRatio,
          tokenDecimals,
          paymentDecimals,
          tokenMin,
          tokenMax,
          fees
        });
        if (!numbers)
          return;
      }
      return numbers;
    }
    return calculateFromInvestmentAmount({
      amount: investmentAmount.toString(),
      tokenPrice,
      lastNAV,
      exchangeRatio,
      tokenDecimals,
      paymentDecimals,
      tokenMin,
      tokenMax,
      investmentMin,
      investmentMax,
      fees
    });
  }
};

export { calculateFromTokenAmount as a, calculateFromTotalAmount as b, calculateFromInvestmentAmount as c };
